import { paymentSequenceLabel, nextPaymentSequenceNumber } from "../../../constants"
import { RULES, ALLOWED_PAYMENT_TYPES, validateFields, fieldError, firstError } from "../../../fieldRules"

const INVOICE_RULES = {
  invoiceSerial: RULES.paymentRequest.invoiceSerial,
  invoiceNumber: RULES.paymentRequest.invoiceNumber,
  supplierTaxCode: RULES.paymentRequest.supplierTaxCode,
}
const BANK_RULES = {
  bankReceiverName: RULES.paymentRequest.bankReceiverName,
  bankNameBranch: RULES.paymentRequest.bankNameBranch,
  bankAccount: RULES.paymentRequest.bankAccount,
}

// Guided create/edit flow. Step 1 is the working form (type, POs with their
// amounts, and the invoice). Step 2 collects the payment fields the table doesn't
// carry (planned date, payment type, payee). Step 3 is read-only review.
export const WIZARD_STEPS = [
  { key: "details", label: "Select Type & Add Details", hint: "Link to POs or create non-PO payment" },
  { key: "payment", label: "Enter Payment Information", hint: "Planned date, payment type, and payee" },
  { key: "review", label: "Review & Submit", hint: "Check and confirm" },
]

export const toDateInput = (value) => {
  if (!value) return ""
  const s = String(value).trim()
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10)
  const m = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/)
  if (!m) return ""
  return `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`
}

export const roundMoney = (n) => Math.round(Number(n) || 0)

export const percentFromAmount = (amount, remaining) => {
  const rem = Number(remaining)
  const amt = Number(amount)
  if (!(rem > 0) || !Number.isFinite(amt)) return ""
  return Math.round((amt / rem) * 10000) / 100
}

export const amountFromPercent = (percent, remaining) => {
  const rem = Number(remaining)
  const pct = Number(percent)
  if (!(rem > 0) || !Number.isFinite(pct)) return ""
  return roundMoney((rem * pct) / 100)
}

const percentText = (raw) => {
  const match = String(raw ?? "").replace(/[^\d.]/g, "").match(/^\d*\.?\d{0,2}/)
  return match ? match[0] : ""
}

export const emptySharedPayment = () => ({
  paymentType: "normal",
  paymentTimeline: "",
  bankReceiverName: "",
  bankNameBranch: "",
  bankAccount: "",
  description: "",
  bankTouched: false,
  batchLabel: "",
})

export const emptyNonPoInvoice = () => ({
  invoiceSerial: "",
  invoiceNumber: "",
  invoiceDate: "",
  supplierTaxCode: "",
  amount: "",
  note: "",
  sellerName: "",
  invoiceAmount: null,
  xmlFile: null,
  xmlStatus: null,
  xmlError: "",
  xmlData: null,
  pdfFile: null,
})

export const purchaseToRow = (po = {}) => {
  const total = po.total ?? po.totalAmount
  const remaining = po.remainingToPay ?? po.remaining
  const hasTotal = total != null && total !== ""
  const hasRemaining = remaining != null && remaining !== ""
  return {
    purchaseId: String(po.id),
    poCode: po.poCode || "",
    supplierName: po.supplierName || po.vendorName || "",
    supplierId: po.vendorId ?? po.supplierId ?? null,
    total: hasTotal ? Number(total) : null,
    remaining: hasRemaining ? Number(remaining) : null,
    previouslyPaid: hasTotal && hasRemaining ? Math.max(0, Number(total) - Number(remaining)) : null,
    amount: "",
    percent: "",
    inputMode: "amount",
    amountTouched: false,
    included: true,
    note: "",
    bankReceiverName: "",
    bankNameBranch: "",
    bankAccount: "",
    existingCount: 0,
    budgetCategoryId: po.budgetCategoryId ?? null,
    region: po.region ?? null,
    center: po.center ?? po.centerId ?? null,
    contextLoaded: false,
    deliveryFee: null,
    headroom: 0,
    amountsLocked: false,
  }
}

const snapshot = (row) =>
  JSON.stringify({
    poCode: row.poCode,
    supplierName: row.supplierName,
    supplierId: row.supplierId,
    total: row.total,
    remaining: row.remaining,
    previouslyPaid: row.previouslyPaid,
    amount: row.amount,
    percent: row.percent,
    budgetCategoryId: row.budgetCategoryId,
    region: row.region,
    center: row.center,
    existingCount: row.existingCount,
    bankReceiverName: row.bankReceiverName,
    bankNameBranch: row.bankNameBranch,
    bankAccount: row.bankAccount,
    contextLoaded: row.contextLoaded,
    deliveryFee: row.deliveryFee,
  })

export const applyPurchaseDetail = (row, purchase) => {
  if (!purchase) return row
  const total = purchase.total ?? purchase.totalAmount ?? row.total
  return {
    ...row,
    poCode: purchase.poCode || row.poCode,
    supplierName: purchase.supplierName || purchase.vendorName || row.supplierName,
    supplierId: purchase.vendorId ?? purchase.supplierId ?? row.supplierId,
    total: total == null ? row.total : Number(total),
    budgetCategoryId: purchase.budgetCategoryId ?? row.budgetCategoryId,
    region: purchase.region ?? row.region,
    center: purchase.center ?? purchase.centerId ?? row.center,
  }
}

// `editHeadroom` adds this draft's own amount back onto remainingToPay.
// The payment-context endpoint already excludes open drafts, so an edit
// would otherwise look over-allocated by exactly the amount being edited.
const syncFigures = (row, remaining) => {
  const next = { ...row, remaining }
  if (!row.amountTouched && remaining != null) {
    next.amount = roundMoney(remaining)
    next.percent = remaining > 0 ? 100 : ""
    return next
  }
  if (row.inputMode === "percent") {
    next.amount =
      row.percent === "" || row.percent == null || row.percent === "." ? "" : amountFromPercent(row.percent, remaining)
    return next
  }
  next.percent = row.amount === "" || row.amount == null || !(remaining > 0) ? "" : percentFromAmount(row.amount, remaining)
  return next
}

export const applyPaymentContext = (row, ctx, editHeadroom = 0) => {
  if (!ctx) return row
  const raw = ctx.remainingToPay
  const remaining = raw == null ? row.remaining : Number(raw) + (Number(editHeadroom) || 0)
  const previouslyPaid =
    row.total != null && remaining != null ? Math.max(0, Number(row.total) - Number(remaining)) : row.previouslyPaid
  let next = syncFigures(
    {
      ...row,
      previouslyPaid,
      existingCount: Array.isArray(ctx.paidBatches) ? ctx.paidBatches.length : row.existingCount,
      contextLoaded: true,
      deliveryFee: ctx.deliveryFee ?? row.deliveryFee,
    },
    remaining
  )
  if (ctx.bankDefault) {
    if (!next.bankReceiverName) next.bankReceiverName = ctx.bankDefault.bankReceiverName || ""
    if (!next.bankNameBranch) next.bankNameBranch = ctx.bankDefault.bankNameBranch || ""
    if (!next.bankAccount) next.bankAccount = ctx.bankDefault.bankAccount || ""
  }
  return next
}

export const mergeHydration = (row, { purchase, ctx } = {}) => {
  let next = applyPurchaseDetail(row, purchase)
  next = applyPaymentContext(next, ctx, row.headroom || 0)
  return snapshot(row) === snapshot(next) ? row : next
}

export const withAmount = (row, raw) => {
  if (row.amountsLocked) return row
  const digits = String(raw ?? "").replace(/[^\d]/g, "")
  const amount = digits === "" ? "" : Number(digits)
  return {
    ...row,
    amount,
    amountTouched: true,
    inputMode: "amount",
    percent: amount === "" ? "" : percentFromAmount(amount, row.remaining),
  }
}

export const withPercent = (row, raw) => {
  if (row.amountsLocked) return row
  const percent = percentText(raw)
  if (!(Number(row.remaining) > 0)) {
    return { ...row, percent, amountTouched: true, inputMode: "percent" }
  }
  const amount = percent === "" || percent === "." ? "" : amountFromPercent(percent, row.remaining)
  return {
    ...row,
    percent,
    amount,
    amountTouched: true,
    inputMode: "percent",
  }
}

export const withInputMode = (row, inputMode) => ({ ...row, inputMode })

export const applyInvoiceXml = (invoice, data, file) => {
  const next = {
    ...invoice,
    xmlFile: file || invoice.xmlFile,
    xmlStatus: "ok",
    xmlError: "",
    xmlData: data,
  }
  if (data.invoiceSerial) next.invoiceSerial = data.invoiceSerial
  if (data.invoiceNumber) next.invoiceNumber = data.invoiceNumber
  if (data.invoiceDate) next.invoiceDate = toDateInput(data.invoiceDate)
  if (data.supplierTaxCode) next.supplierTaxCode = data.supplierTaxCode
  if (data.sellerName) next.sellerName = data.sellerName
  if (data.amount != null && data.amount !== "") next.invoiceAmount = Number(data.amount)
  if (!invoice.note && Array.isArray(data.items) && data.items.length) {
    const shown = data.items.slice(0, 5).join("; ")
    const more = data.items.length > 5 ? ` (+${data.items.length - 5} more)` : ""
    next.note = `HĐ ${data.invoiceSerial || ""}-${data.invoiceNumber || ""}: ${shown}${more}`.replace(/^HĐ -/, "HĐ")
  }
  return next
}

export const includedRows = (rows = []) => rows.filter((r) => r.included)

export const totalsOf = (rows = []) => {
  const list = includedRows(rows)
  const sum = (pick) => list.reduce((s, r) => s + (Number(pick(r)) || 0), 0)
  return {
    count: list.length,
    poTotal: sum((r) => r.total),
    previouslyPaid: sum((r) => r.previouslyPaid),
    remaining: sum((r) => r.remaining),
    payment: sum((r) => r.amount),
  }
}

export const sameSupplier = (a, b) => {
  if (!a || !b) return true
  const aId = a.supplierId ?? a.vendorId
  const bId = b.supplierId ?? b.vendorId
  if (aId != null && aId !== "" && bId != null && bId !== "") return String(aId) === String(bId)
  const aName = a.supplierName || a.vendorName || ""
  const bName = b.supplierName || b.vendorName || ""
  if (!aName || !bName) return true
  return aName === bName
}

// Per-row rules for the amount/% pair. Over-remaining
// is blocked here: the % control is defined as a share of what's left, so a
// value past 100% isn't a payment the row can express. (The backend still
// accepts a modest overage — this screen doesn't send one.)
export const validatePoRow = (row) => {
  if (!row || !row.included || row.amountsLocked) return {}
  const e = {}
  const amount = Number(row.amount)
  const percent = Number(row.percent)
  if (row.amount === "" || row.amount == null || !Number.isFinite(amount) || !(amount > 0)) {
    e.amount = "Payment amount must be greater than 0."
  } else if (amount < 0) {
    e.amount = "Payment amount cannot be negative."
  }
  if (row.percent !== "" && row.percent != null && row.percent !== "." && Number.isFinite(percent) && percent < 0) {
    e.percent = "Percentage cannot be negative."
  }
  if (row.percent !== "" && row.percent !== "." && Number.isFinite(percent) && percent > 100) {
    e.percent = "Percentage cannot exceed 100%."
  }
  if (row.remaining != null && Number.isFinite(amount) && amount > Number(row.remaining) + 0.001) {
    e.amount = "Payment cannot exceed the remaining amount."
    if (!e.percent) e.percent = "Percentage cannot exceed 100%."
  }
  return e
}

export const validateInvoice = (invoice, { paymentType } = {}) => {
  const e = validateFields(invoice, INVOICE_RULES)
  if (paymentType !== "deposit" && !String(invoice?.invoiceNumber || "").trim() && !e.invoiceNumber) {
    e.invoiceNumber = "Invoice number is required unless Payment Type is Deposit."
  }
  return e
}

export const validateNonPo = (nonPo, { paymentType, description, descriptionRequired, amount } = {}) => {
  const e = validateFields(nonPo, INVOICE_RULES)
  const amt = Number(amount)
  if (!(amt > 0)) e.amount = "Payment amount must be greater than 0."
  if (descriptionRequired && !String(description || "").trim()) {
    e.description =
      "Description is required for a payment without a Purchase Order or one that exceeds the PO's remaining balance."
  }
  return e
}

export const validateShared = (shared) => {
  const e = validateFields(shared, { ...BANK_RULES, batchLabel: RULES.paymentRequest.batchLabel })
  if (!shared?.paymentTimeline) e.paymentTimeline = "Planned payment date is required. Add it on Enter Payment Information."
  if (shared?.paymentType && !ALLOWED_PAYMENT_TYPES.includes(shared.paymentType)) {
    e.paymentType = "Payment type này chưa được hỗ trợ — chọn Normal Payment hoặc Deposit."
  }
  return e
}

export const bankFor = (row, shared) => {
  if (shared?.bankTouched) {
    return {
      bankReceiverName: shared.bankReceiverName || "",
      bankNameBranch: shared.bankNameBranch || "",
      bankAccount: shared.bankAccount || "",
    }
  }
  return {
    bankReceiverName: row?.bankReceiverName || shared?.bankReceiverName || "",
    bankNameBranch: row?.bankNameBranch || shared?.bankNameBranch || "",
    bankAccount: row?.bankAccount || shared?.bankAccount || "",
  }
}

const invoiceFields = (invoice = {}) => ({
  invoiceSerial: invoice.invoiceSerial || "",
  invoiceNumber: invoice.invoiceNumber || "",
  invoiceDate: invoice.invoiceDate || "",
  supplierTaxCode: invoice.supplierTaxCode || "",
})

// One payment request stores one invoice, however many POs it pays.
export const buildPoLine = (row, shared = {}, invoice = {}) => ({
  batchLabel: shared.batchLabel || paymentSequenceLabel(nextPaymentSequenceNumber(row.existingCount || 0)),
  paymentType: shared.paymentType || "normal",
  amount: Number(row.amount) || 0,
  paymentTimeline: shared.paymentTimeline || "",
  ...bankFor(row, shared),
  ...invoiceFields(invoice),
  description: shared.description || "",
  note: row.note || "",
})

export const buildNonPoLine = ({ shared = {}, invoice = {}, amount } = {}) => ({
  batchLabel: shared.batchLabel || "",
  paymentType: shared.paymentType || "normal",
  amount: Number(amount) || 0,
  paymentTimeline: shared.paymentTimeline || "",
  bankReceiverName: shared.bankReceiverName || "",
  bankNameBranch: shared.bankNameBranch || "",
  bankAccount: shared.bankAccount || "",
  ...invoiceFields(invoice),
  description: shared.description || "",
  note: invoice.note || "",
})

export const collectUploads = (source, additionalDocs = []) => {
  const files = []
  if (source?.xmlStatus === "ok" && source.xmlFile) files.push({ file: source.xmlFile, docType: "invoice_xml" })
  if (source?.pdfFile) files.push({ file: source.pdfFile, docType: "invoice_pdf" })
  additionalDocs.forEach((file) => {
    if (file) files.push({ file, docType: "other" })
  })
  return files
}

let nonPoLineSeq = 0
export const newNonPoLine = (centerId = "") => ({
  key: `non-po-line-${(nonPoLineSeq += 1)}`,
  centerId,
  itemId: null,
  itemName: "",
  isCustomItem: false,
  customItemName: "",
  offCatalogUnit: "",
  offCatalogDescription: "",
  amount: "",
})

export const emptyNonPoSpend = () => ({
  title: "",
  budgetCategoryId: "",
  region: "",
  departmentId: "",
  reason: "",
  lines: [newNonPoLine()],
})

export const nonPoSpendTotal = (lines = []) => lines.reduce((sum, l) => sum + (Number(l.amount) || 0), 0)

const isNonPoLineValid = (line) =>
  !!line.centerId && Number(line.amount) > 0 && (line.isCustomItem ? !!line.customItemName?.trim() : !!line.itemId)

export const nonPoSpendGap = (state) => {
  const missing = []
  if (!state.title?.trim()) missing.push("a title")
  if (!state.budgetCategoryId) missing.push("a budget category")
  if (!(state.lines || []).length) missing.push("at least 1 line")
  else if (!state.lines.every(isNonPoLineValid)) missing.push("a center, an item and an amount on every line")
  if (missing.length) return `Still needed: ${missing.join(", ")}.`
  const titleError = fieldError(state.title, RULES.orderRequest.title)
  if (titleError) return `Title: ${titleError}`
  for (const [idx, line] of state.lines.entries()) {
    if (!line.isCustomItem) continue
    const lineError = firstError(
      validateFields(line, {
        customItemName: RULES.orderRequestLine.offCatalogItemName,
        offCatalogUnit: RULES.orderRequestLine.offCatalogUnit,
      })
    )
    if (lineError) return `Line ${idx + 1}: ${lineError}`
  }
  return null
}

// Wire `directOrder` for POST /payment-requests. Each line carries only its
// total (quantity 1 × unitPrice), and no vendor is sent with it.
export const buildNonPoSpendPayload = (state) => ({
  title: state.title.trim(),
  budgetCategoryId: state.budgetCategoryId,
  departmentId: state.departmentId || undefined,
  region: state.region || undefined,
  reason: state.reason?.trim() || undefined,
  lines: (state.lines || []).map((line) => ({
    itemId: line.isCustomItem ? undefined : line.itemId,
    offCatalogItemName: line.isCustomItem ? line.customItemName?.trim() || undefined : undefined,
    offCatalogUnit: line.isCustomItem ? line.offCatalogUnit?.trim() || undefined : undefined,
    offCatalogDescription: line.isCustomItem ? line.offCatalogDescription?.trim() || undefined : undefined,
    centerId: line.centerId,
    requestType: "new",
    quantity: 1,
    unitPrice: Number(line.amount) || 0,
  })),
})

// Detail `directOrder` → builder state, for editing a Non-PO draft. PUT
// replaces the whole line set, so every saved line is carried back.
export const directOrderToState = (directOrder = {}) => {
  const lines = (directOrder.lines || []).map((l) => ({
    ...newNonPoLine(l.centerId ?? ""),
    itemId: l.itemId ?? null,
    itemName: l.itemName || "",
    isCustomItem: !l.itemId && !!l.offCatalogItemName,
    customItemName: l.offCatalogItemName || "",
    offCatalogUnit: l.offCatalogUnit || "",
    offCatalogDescription: l.offCatalogDescription || "",
    amount: String(l.amount ?? (Number(l.quantity) || 0) * (Number(l.unitPrice) || 0)),
  }))
  return {
    title: directOrder.title || "",
    budgetCategoryId: directOrder.budgetCategoryId ?? "",
    region: directOrder.region || "",
    departmentId: directOrder.departmentId ?? "",
    reason: directOrder.reason || "",
    lines: lines.length ? lines : [newNonPoLine()],
  }
}

// `statuses`: { [centerId]: { loading, missing, exceeds } } reported by the
// per-center budget checks on the review step.
export const nonPoBudgetIssue = (statuses = {}) => {
  const list = Object.values(statuses)
  if (!list.length || list.some((s) => s.loading)) return "Budget check is still running. Please wait a moment."
  if (list.some((s) => s.missing)) return "No active budget was found for this category and center. Ask Procurement/Admin to set it up before submitting."
  if (list.some((s) => s.exceeds)) return "This payment exceeds the remaining budget of its category."
  return null
}
